import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InterestRegisterFacadeService } from 'app/services/api/interest-register/interest-register.service';
import { INTEREST_REGISTER_INFO_TEXTS, InterestRegisterInfoTextConfig } from 'common/constants/assists-interest-register-detail.constants';
import { InterestRegisterEntryType } from 'common/constants/assists-interest-register.constants';

@Component({
    selector: 'app-interest-register-form',
    templateUrl: './interest-register-form.component.html',
    styleUrls: ['./interest-register-form.component.scss'],
})
export class InterestRegisterFormComponent implements OnInit {
    form!: FormGroup;
    entryType!: InterestRegisterEntryType;
    selectedFiles: File[] = [];
    isDraggingAttachment = false;
    attachmentError: string = '';
    isSubmitting = false;
    InterestRegisterEntryType = InterestRegisterEntryType;
    infoTextConfig?: InterestRegisterInfoTextConfig;
    showMoreInformation = false;

    private readonly allowedAttachmentExtensions = [
        '.pdf',
        '.jpg',
        '.jpeg',
        '.png',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.msg',
        '.eml',
        '.txt',
    ];
    private readonly maxAttachmentCount = 5;
    private readonly maxAttachmentSizeBytes = 50 * 1024 * 1024;

    constructor(
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly facade: InterestRegisterFacadeService
    ) {}

    ngOnInit(): void {
        this.entryType = this.route.snapshot.paramMap.get('entryType') as InterestRegisterEntryType;
        this.infoTextConfig = INTEREST_REGISTER_INFO_TEXTS[this.entryType];
        this.form = this.createForm(this.entryType);
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const attachmentValidationError = this.getAttachmentValidationError(this.selectedFiles);
        if (attachmentValidationError) {
            this.attachmentError = attachmentValidationError;
            return;
        }

        this.isSubmitting = true;

        this.facade
            .createEntry({
                type: this.entryType,
                formData: this.form.getRawValue(),
                attachments: this.selectedFiles,
            })
            .subscribe({
                next: () => {
                    this.isSubmitting = false;
                    this.router.navigate(['../../minar-skraningar'], { relativeTo: this.route });
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.attachmentError = typeof error === 'string' ? error : 'Ekki tókst að vista skráningu.';
                },
            });
    }

    cancel(): void {
        this.router.navigate(['../../'], { relativeTo: this.route });
    }

    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);

        this.addFiles(files);

        input.value = '';
    }

    onAttachmentDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingAttachment = true;
    }

    onAttachmentDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingAttachment = false;
    }

    onAttachmentDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingAttachment = false;

        const files = this.getDroppedFiles(event);

        if (files.length === 0) {
            this.attachmentError =
                'Ekki tókst að lesa skrána. Ef þú ert að draga tölvupóst beint úr Outlook þarf að vista eða draga hann fyrst á skjáborð sem .msg eða .eml skrá og setja hann síðan inn sem viðhengi.';
            return;
        }

        this.addFiles(files);
    }

    private getDroppedFiles(event: DragEvent): File[] {
        if (event.dataTransfer?.files?.length) {
            return Array.from(event.dataTransfer.files);
        }

        const files: File[] = [];

        if (event.dataTransfer?.items?.length) {
            for (const item of Array.from(event.dataTransfer.items)) {
                if (item.kind !== 'file') {
                    continue;
                }

                const file = item.getAsFile();

                if (file) {
                    files.push(file);
                }
            }
        }

        return files;
    }

    private addFiles(files: File[]): void {
        this.attachmentError = '';

        const normalizedFiles = files.map((file) => this.normalizeOutlookFileIfPossible(file));
        const validationError = this.getAttachmentValidationError(normalizedFiles);

        if (validationError) {
            this.attachmentError = validationError;
            return;
        }

        this.selectedFiles = [...this.selectedFiles, ...normalizedFiles];
    }

    private normalizeOutlookFileIfPossible(file: File): File {
        const extension = this.getFileExtension(file.name);

        if (extension) {
            return file;
        }

        if (file.type === 'application/vnd.ms-outlook') {
            return new File([file], `tolvupostur-${new Date().getTime()}.msg`, {
                type: file.type,
                lastModified: file.lastModified,
            });
        }

        return file;
    }

    private getAttachmentValidationError(files: File[]): string | null {
        if (!files || files.length === 0) {
            return null;
        }

        if (this.selectedFiles.length + files.length > this.maxAttachmentCount) {
            return 'Hámark ' + this.maxAttachmentCount + ' viðhengi eru leyfð.';
        }

        for (const file of files) {
            if (!file) {
                return 'Viðhengi er ekki gilt.';
            }

            if (file.size === 0) {
                return 'Viðhengi má ekki vera tómt: ' + file.name;
            }

            if (file.size > this.maxAttachmentSizeBytes) {
                return 'Viðhengi er of stórt: ' + file.name;
            }

            const extension = this.getFileExtension(file.name);

            if (!extension || !this.allowedAttachmentExtensions.includes(extension)) {
                return 'Óleyfileg skráarending á viðhengi: ' + file.name;
            }
        }

        return null;
    }

    private getFileExtension(fileName: string): string {
        const lastDotIndex = fileName.lastIndexOf('.');

        if (lastDotIndex < 0) {
            return '';
        }

        return fileName.substring(lastDotIndex).toLowerCase();
    }

    removeFile(file: File): void {
        this.selectedFiles = this.selectedFiles.filter((selectedFile) => selectedFile !== file);

        if (this.selectedFiles.length === 0) {
            this.attachmentError = '';
        }
    }

    getTitle(): string {
        switch (this.entryType) {
            case InterestRegisterEntryType.ConflictOfInterest:
                return 'Skráning hagsmunaárekstra';
            case InterestRegisterEntryType.GiftOrTravel:
                return 'Gjafir, boð og starfstengdar boðsferðir';
            case InterestRegisterEntryType.EmploymentOrBoardParticipation:
                return 'Atvinnuþátttaka, atvinnustarfsemi og stjórnarseta';
            case InterestRegisterEntryType.SecuritiesTrading:
                return 'Viðskipti með fjármálagerninga';
            case InterestRegisterEntryType.InterDepartmentTrading:
                return 'Viðskipti milli deilda LSR';
            default:
                return 'Ný skráning';
        }
    }

    private createForm(type: InterestRegisterEntryType): FormGroup {
        switch (type) {
            case InterestRegisterEntryType.ConflictOfInterest:
                return this.fb.group({
                    area: ['', Validators.required],
                    projectType: ['', Validators.required],
                    description: ['', [Validators.required, Validators.maxLength(2000)]],
                    conflictingRelations: ['', [Validators.required, Validators.maxLength(2000)]],
                    measuresTaken: ['', [Validators.required, Validators.maxLength(2000)]],
                });

            case InterestRegisterEntryType.GiftOrTravel:
                return this.fb.group({
                    type: ['', Validators.required],
                    offeredBy: ['', [Validators.required, Validators.maxLength(255)]],
                    description: ['', [Validators.required, Validators.maxLength(2000)]],
                    date: ['', Validators.required],
                    estimatedValue: [null, [Validators.min(0)]],
                });

            case InterestRegisterEntryType.EmploymentOrBoardParticipation:
                return this.fb.group({
                    participationType: ['', Validators.required],
                    memberRole: [''],
                    startOrEnd: ['', Validators.required],
                    isRelatedToLsrRole: [null, Validators.required],
                    compensation: [null, Validators.min(0)],
                    date: ['', Validators.required],
                    companyName: ['', [Validators.required, Validators.maxLength(255)]],
                    companyType: ['', Validators.maxLength(255)],
                    otherRelations: ['', Validators.maxLength(2000)],
                    otherDescription: ['', Validators.maxLength(2000)],
                });

            case InterestRegisterEntryType.SecuritiesTrading:
                return this.fb.group({
                    transactionDate: ['', Validators.required],
                    transactionType: ['', Validators.required],
                    transactionOwnerName: [''],
                    financialInstrumentName: ['', [Validators.required, Validators.maxLength(255)]],
                    isPublicOffering: [false],
                    nominalValue: [null, Validators.min(0)],
                    transactionPrice: [null, Validators.min(0)],
                    otherInformation: ['', Validators.maxLength(2000)],
                });

            case InterestRegisterEntryType.InterDepartmentTrading:
                return this.fb.group({
                    transactionDate: ['', Validators.required],
                    buyer: ['', Validators.required],
                    seller: ['', Validators.required],
                    financialInstrumentName: ['', [Validators.required, Validators.maxLength(255)]],
                    nominalValue: [null, [Validators.required, Validators.min(0)]],
                    yieldOrPrice: [null, [Validators.required, Validators.min(0)]],
                    transactionTermsReference: ['', [Validators.required, Validators.maxLength(2000)]],
                    interestAssessment: ['', Validators.maxLength(4000)],
                });

            default:
                return this.fb.group({});
        }
    }
}
